/**
 * Pure lifecycle logic for the Semble MDT Report document row.
 *
 * Kept dependency-free (no firebase/context imports) so it can be unit-tested
 * directly with no mocking, per the project testing guidelines.
 *
 * The row is keyed on Diagnostic Decision completion (the report summarises
 * the finished assessment, and the MDT Plan inputs are captured on the final
 * DD page), and mirrors the standard document-row lifecycle:
 *
 *   'locked'           — grey   — Diagnostic Decision not yet complete (lock icon).
 *   'processing'       — amber  — the AI paragraphs are generating, or the
 *                                  export document is rendering.
 *   'ready-for-review' — violet — AI paragraphs generated, awaiting clinician
 *                                  review + approval ("AI outputs ready for review").
 *   'approved'         — green  — all paragraphs approved; Preview + Download
 *                                  the rendered document.
 *   'not-generated'    — grey   — DD complete but no paragraphs yet: shown with
 *                                  a manual Generate button.  This covers legacy
 *                                  patients who completed the DD before this
 *                                  feature shipped, and staleness resets (e.g.
 *                                  the CI was regenerated after DD completion,
 *                                  which clears sembleMDTReportGenerated).
 *   'error'            — red    — generation/render failed (Retry button).
 *   'pdf-pending'      — amber  — paragraphs approved and the DOCX rendered,
 *                                  but the PDF export hit a transient Drive or
 *                                  Storage fault and is being retried in the
 *                                  background ("Preparing PDF").  No actions
 *                                  are offered: nothing is wrong that the
 *                                  clinician needs to fix, and there is no PDF
 *                                  to preview or download yet.
 *
 * Generation is normally triggered automatically by the DD completion flow, so
 * 'not-generated' is transient for new patients — but keeping it as an explicit,
 * actionable state (rather than pretending to process) means a missed/failed
 * auto-trigger never leaves the row stuck.
 *
 * PDF overlay: once the row would read 'approved', the rendered document's
 * `pdfStatus` (via getPdfAvailability) is overlaid, mirroring
 * buildDashboardProcessingStatuses on the main Documents Dashboard.  Without
 * this the render Cloud Function's "PDF pending, section complete" write
 * would leave the row green with the Preview and Download buttons silently
 * missing.  The overlay applies only in the approved state because the
 * document entry describes the *approved* render; while paragraphs are being
 * regenerated or re-reviewed, a stale pdfStatus from a previous render must
 * not colour the row.
 */

/**
 * @param {{ ddComplete?: boolean, processing?: boolean, error?: boolean,
 *           generated?: boolean, approved?: boolean,
 *           pdfAvailability?: 'ready'|'pending'|'error'|'missing' }} args
 *   ddComplete      — 4_diagnosticDecision.status === 'completed'
 *   processing      — sembleMDTReportProcessingStatus === 'processing'
 *   error           — sembleMDTReportProcessingStatus === 'error'
 *   generated       — sembleMDTReportGenerated === true (AI paragraphs exist)
 *   approved        — sembleMDTReportApproved === true (all paragraphs approved)
 *   pdfAvailability — getPdfAvailability(documents.semblemdtreportReport);
 *                     optional so existing callers are unaffected
 * @returns {'locked'|'processing'|'ready-for-review'|'approved'|'not-generated'|'error'|'pdf-pending'}
 */
export const getSembleMdtReportRowStatus = ({
  ddComplete, processing, error, generated, approved, pdfAvailability,
} = {}) => {
  if (!ddComplete) return 'locked';
  if (error) return 'error';
  if (processing) return 'processing';
  // approved/generated only count while generated is still true — a staleness
  // reset (CI regenerated) clears both flags, dropping the row back to
  // 'not-generated' so the clinician regenerates and re-reviews.
  if (approved && generated) {
    // 'error': the PDF step failed permanently (or the background retries
    // were exhausted) — same red row + Try again as a processing error, and
    // Try again re-renders the approved document rather than regenerating.
    if (pdfAvailability === 'error') return 'error';
    // 'pending': queued for background retry — amber, no actions.
    if (pdfAvailability === 'pending') return 'pdf-pending';
    // 'ready', 'missing' (legacy entry or not yet rendered) and undefined
    // (caller did not supply it) all fall through to the normal green row.
    return 'approved';
  }
  if (generated) return 'ready-for-review';
  return 'not-generated';
};
